/**
 * Text normalization utilities for handling special characters
 * that may not render correctly in the UI (appearing as ⊠ or similar)
 */

/**
 * Normalizes text by replacing problematic Unicode characters with their
 * ASCII equivalents. This fixes issues where smart quotes, special dashes,
 * control characters, and other characters appear as replacement characters (⊠) in the UI.
 */
export function normalizeText(text: string | null | undefined): string {
  if (!text) return '';

  return (
    text
      // CRITICAL: ASCII control characters (0x00-0x1F) - these cause ⊠ display issues
      // \u001f (Unit Separator) is commonly misused as apostrophe or dash
      // eslint-disable-next-line no-control-regex
      .replace(/\u001f(?=s\s|s\b|s$|s[.,;:!?)])/gi, "'")
      // eslint-disable-next-line no-control-regex
      .replace(/\u001f/g, ' - ')
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001E]/g, '')
      // eslint-disable-next-line no-control-regex
      .replace(/\u0011/g, '-')
      // Smart quotes (curly quotes) - single
      .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
      // Smart quotes (curly quotes) - double
      .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
      // Various dashes to standard hyphen-minus
      .replace(/[\u2010\u2011\u2012\u2013\u2014\u2015\u2212]/g, '-')
      // Horizontal ellipsis
      .replace(/\u2026/g, '...')
      // Non-breaking space
      .replace(/\u00A0/g, ' ')
      // Soft hyphen (invisible)
      .replace(/\u00AD/g, '')
      // Zero-width characters
      .replace(/[\u200B\u200C\u200D\uFEFF]/g, '')
      // Bullet characters to standard bullet or dash
      .replace(/[\u2022\u2023\u2043\u25AA\u25AB\u25CF\u25E6\u29BE\u29BF]/g, '•')
      // Section symbol variations
      .replace(/[\u00A7]/g, '§')
      // Copyright symbol
      .replace(/[\u00A9]/g, '(c)')
      // Registered trademark
      .replace(/[\u00AE]/g, '(R)')
      // Trademark
      .replace(/[\u2122]/g, '(TM)')
      // Degree symbol
      .replace(/[\u00B0]/g, '°')
      // Box drawing and replacement characters - convert to space or remove
      .replace(/[\u2588\u2591\u2592\u2593\uFFFD]/g, ' ')
      // Mathematical operators that might cause issues
      .replace(/[\u00D7]/g, 'x') // multiplication sign
      .replace(/[\u00F7]/g, '/') // division sign
      // Common fractions
      .replace(/\u00BD/g, '1/2')
      .replace(/\u00BC/g, '1/4')
      .replace(/\u00BE/g, '3/4')
      .replace(/\u2153/g, '1/3')
      .replace(/\u2154/g, '2/3')
      // Superscript numbers
      .replace(/\u00B9/g, '1')
      .replace(/\u00B2/g, '2')
      .replace(/\u00B3/g, '3')
      // Arrows to text
      .replace(/[\u2190]/g, '<-')
      .replace(/[\u2192]/g, '->')
      .replace(/[\u2194]/g, '<->')
      .replace(/[\u21D2]/g, '=>')
      // Clean up any remaining problematic characters
      // This catches characters in the Private Use Area and other unusual ranges
      .replace(/[\uE000-\uF8FF]/g, '') // Private Use Area
      .replace(/[\uD800-\uDFFF]/g, '') // Surrogate pairs (malformed)
      // Normalize multiple spaces (but preserve single spaces)
      .replace(/  +/g, ' ')
      .trim()
  );
}

/**
 * Normalizes text while preserving paragraph breaks (double newlines)
 */
export function normalizeTextPreserveNewlines(text: string | null | undefined): string {
  if (!text) return '';

  // Split by paragraph breaks first
  const paragraphs = text.split(/\n\s*\n/);

  // Normalize each paragraph
  const normalizedParagraphs = paragraphs.map((p) => normalizeText(p));

  // Rejoin with double newlines
  return normalizedParagraphs.join('\n\n');
}

/**
 * Checks if text contains potentially problematic characters
 * Useful for debugging encoding issues
 */
export function hasProblematicCharacters(text: string): boolean {
  // Pattern matches common problematic Unicode ranges
  const problematicPattern = /[\u2018-\u201F\u2010-\u2015\u2026\u2022\u25CF\uFFFD\uE000-\uF8FF]/;
  return problematicPattern.test(text);
}
